/**
 * /packages — All-packages browse page.
 *
 * - Reads services from CatalogContext (already cached) and packages from
 *   GET /api/packages (no backend changes).
 * - Filter chips: All / each service. "All" is the default.
 * - 3-col desktop, 2-col tablet, 1-col mobile. No horizontal overflow at 375px.
 * - Skeleton loaders + empty state.
 */
import React, { useEffect, useMemo, useState } from "react";

import { useCatalog } from "../lib/catalog-context";
import { listPackages, listPortfolioFeatured } from "../lib/api";
import PackageCard from "../components/shared/PackageCard";
import { SkeletonCard } from "../components/shared/Skeleton";
import EmptyState from "../components/shared/EmptyState";

export default function PackagesList() {
  const { services, loading: catalogLoading } = useCatalog();
  const [packages, setPackages] = useState([]);
  const [covers, setCovers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeServiceId, setActiveServiceId] = useState("all");

  // Load all packages once.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    listPackages({})
      .then((rows) => {
        if (!cancelled) setPackages(rows || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "Couldn't load packages.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Pull a cover image per service so we can decorate cards that have no
  // hero_image_url. Same pattern as ServicesList / Home rail.
  useEffect(() => {
    if (!services?.length) return undefined;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        services.map(async (svc) => {
          try {
            const imgs = await listPortfolioFeatured({ service_id: svc.id, limit: 1 });
            return [svc.id, imgs[0] || null];
          } catch {
            return [svc.id, null];
          }
        }),
      );
      if (!cancelled) setCovers(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [services]);

  const filtered = useMemo(() => {
    if (activeServiceId === "all") return packages;
    return packages.filter((p) => p.service_id === activeServiceId);
  }, [packages, activeServiceId]);

  const chips = useMemo(
    () => [
      { id: "all", name: "All" },
      ...(services || []).map((s) => ({ id: s.id, name: s.name })),
    ],
    [services],
  );

  return (
    <section
      data-testid="packages-list-page"
      className="mx-auto max-w-6xl px-5 pt-20 pb-24 sm:px-6 sm:pt-28"
    >
      <p className="mb-4 text-xs uppercase tracking-[0.32em] text-[#C8A24B]">
        ShutterKART
      </p>
      <h1 className="font-display text-4xl text-[#1A1A1A] sm:text-5xl lg:text-6xl">
        All packages
      </h1>
      <p className="mt-4 max-w-xl text-base text-[#1A1A1A]/65">
        Curated coverage tiers across weddings, portraits, and celebrations.
        Every package is a starting point — your final quote is tailored to you.
      </p>

      {/* Filter chips */}
      <div
        data-testid="packages-filter-chips"
        className="-mx-5 mt-10 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0"
      >
        {chips.map((c) => {
          const active = activeServiceId === c.id;
          return (
            <button
              key={c.id}
              type="button"
              data-testid={`packages-chip-${c.id}`}
              onClick={() => setActiveServiceId(c.id)}
              aria-pressed={active}
              className={[
                "shrink-0 rounded-full border px-4 py-2 text-xs uppercase tracking-[0.18em] transition",
                active
                  ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FAF7F2]"
                  : "border-[#1A1A1A]/15 text-[#1A1A1A]/75 hover:border-[#C8A24B] hover:text-[#C8A24B]",
              ].join(" ")}
            >
              {c.name}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      <div className="mt-10">
        {loading || catalogLoading ? (
          <ul
            data-testid="packages-list-skeleton"
            className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <li key={i}>
                <SkeletonCard />
              </li>
            ))}
          </ul>
        ) : error ? (
          <EmptyState
            title="Couldn't load packages"
            subtitle={error}
            testid="packages-list-error"
          />
        ) : !filtered.length ? (
          <EmptyState
            title="No packages in this category yet"
            subtitle="Try another filter or browse all."
            testid="packages-list-empty"
          />
        ) : (
          <ul
            data-testid="packages-list-grid"
            className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {filtered.map((p) => (
              <li key={p.id}>
                <PackageCard pkg={p} coverImage={covers[p.service_id]} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
